'use client';

import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';

interface UserStatsParams {
  /** A super admin's picked PPJK; the server ignores it for everyone else. */
  ppjkNpwp?: string;
  startDate?: string;
  endDate?: string;
  offset: number;
  limit: number;
  orderBy: string;
  ascending: boolean;
}

interface UserStatsItem {
  userId: number;
  userName: string;
  ppjkName: string;
  totalAju: number;
  firstAju: string;
  lastAju: string;
}

interface UserStatsResponse {
  success: boolean;
  message: string;
  data: {
    data: UserStatsItem[];
    total: number;
  };
}

interface UserStatsResult {
  data: UserStatsItem[];
  totalCount: number;
}

async function fetchUserStats({
  ppjkNpwp,
  startDate,
  endDate,
  offset,
  limit,
  orderBy,
  ascending,
  signal,
}: UserStatsParams & { signal?: AbortSignal }): Promise<UserStatsResult> {
  if (!startDate || !endDate) {
    return { data: [], totalCount: 0 };
  }

  const requestPayload = {
    ppjkNpwp,
    startDate,
    endDate,
    offset,
    limit,
    orderBy,
    ascending,
  };

  const response = await axiosInstance.post<UserStatsResponse>('/api/v1/dashboard/user-aju-stats', requestPayload, {
    signal,
  });

  if (response.data.success === true) {
    return {
      data: response.data.data.data || [],
      totalCount: response.data.data.total || 0,
    };
  }

  throw new Error('Failed to fetch user statistics');
}

export default function useGetUserStats(params: UserStatsParams) {
  const queryResult = useQuery({
    queryKey: [
      'user-stats',
      {
        ppjkNpwp: params.ppjkNpwp,
        startDate: params.startDate,
        endDate: params.endDate,
        offset: params.offset,
        limit: params.limit,
        orderBy: params.orderBy,
        ascending: params.ascending,
      },
    ],
    queryFn: ({ signal }) => fetchUserStats({ ...params, signal }),
    enabled: Boolean(params.startDate && params.endDate),
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    data: queryResult.data?.data ?? [],
    totalCount: queryResult.data?.totalCount ?? 0,
    loading: queryResult.isLoading || queryResult.isFetching,
    error: queryResult.error?.message ?? null,
    refetch: queryResult.refetch,
  };
}

export type { UserStatsItem, UserStatsParams };
